import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { FooterColumn, FooterContent, FooterLink } from '../../types';
import { FOOTER_ICONS } from '../../components/layout/Footer';
import { extractApiErrorMessage } from '../../utils/errorHandler';

const ICON_OPTIONS = Object.keys(FOOTER_ICONS);

const AdminFooterPage: React.FC = () => {
  const [content, setContent] = useState<FooterContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.SITE_SETTINGS}/footer`);
      setContent(response.data.footer as FooterContent);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load footer content'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!content) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await apiClient.put(`${API_ENDPOINTS.SITE_SETTINGS}/footer`, {
        footer: content
      });
      setContent(response.data.footer as FooterContent);
      window.dispatchEvent(new Event('footer-updated'));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to save footer content'));
    } finally {
      setSaving(false);
    }
  };

  const patch = (updater: (prev: FooterContent) => FooterContent) =>
    setContent((prev) => (prev ? updater(prev) : prev));

  const setColumn = (columnId: string, changes: Partial<FooterColumn>) =>
    patch((prev) => ({
      ...prev,
      columns: prev.columns.map((c) => (c.id === columnId ? { ...c, ...changes } : c))
    }));

  const setLink = (columnId: string, linkId: string, changes: Partial<FooterLink>) =>
    patch((prev) => ({
      ...prev,
      columns: prev.columns.map((c) =>
        c.id === columnId
          ? { ...c, links: c.links.map((l) => (l.id === linkId ? { ...l, ...changes } : l)) }
          : c
      )
    }));

  const addLink = (columnId: string) =>
    patch((prev) => ({
      ...prev,
      columns: prev.columns.map((c) =>
        c.id === columnId
          ? {
              ...c,
              links: [
                ...c.links,
                {
                  id: `link-${Date.now()}`,
                  label: 'New link',
                  href: '/products',
                  visible: true,
                  order: c.links.length + 1
                }
              ]
            }
          : c
      )
    }));

  const removeLink = (columnId: string, linkId: string) =>
    patch((prev) => ({
      ...prev,
      columns: prev.columns.map((c) =>
        c.id === columnId ? { ...c, links: c.links.filter((l) => l.id !== linkId) } : c
      )
    }));

  const moveLink = (columnId: string, linkId: string, direction: -1 | 1) =>
    patch((prev) => ({
      ...prev,
      columns: prev.columns.map((c) => {
        if (c.id !== columnId) return c;
        const sorted = [...c.links].sort((a, b) => a.order - b.order);
        const index = sorted.findIndex((l) => l.id === linkId);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= sorted.length) return c;
        [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
        return { ...c, links: sorted.map((l, i) => ({ ...l, order: i + 1 })) };
      })
    }));

  const addColumn = () =>
    patch((prev) => ({
      ...prev,
      columns: [
        ...prev.columns,
        {
          id: `column-${Date.now()}`,
          heading: 'New column',
          visible: true,
          order: prev.columns.length + 1,
          links: []
        }
      ]
    }));

  const removeColumn = (columnId: string) =>
    patch((prev) => ({ ...prev, columns: prev.columns.filter((c) => c.id !== columnId) }));

  const setBadge = (badgeId: string, changes: Partial<{ text: string; icon: string; visible: boolean }>) =>
    patch((prev) => ({
      ...prev,
      brand: {
        ...prev.brand,
        trustBadges: prev.brand.trustBadges.map((b) => (b.id === badgeId ? { ...b, ...changes } : b))
      }
    }));

  const setContactField = (field: string, value: string) =>
    patch((prev) => ({ ...prev, contact: { ...prev.contact, [field]: value } }));

  const setBottomLink = (linkId: string, changes: Partial<FooterLink>) =>
    patch((prev) => ({
      ...prev,
      bottom: {
        ...prev.bottom,
        links: prev.bottom.links.map((l) => (l.id === linkId ? { ...l, ...changes } : l))
      }
    }));

  if (loading || !content) {
    return (
      <Box>
        <Skeleton width={240} height={40} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Footer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Link columns, contact details and trust badges shown on every page
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save & publish'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Footer updated — changes are live.
        </Alert>
      )}

      {/* Brand + trust badges */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Brand blurb & trust badges
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Description under the logo"
          value={content.brand.description}
          onChange={(e) =>
            patch((prev) => ({ ...prev, brand: { ...prev.brand, description: e.target.value } }))
          }
          sx={{ mb: 2.5 }}
        />
        {content.brand.trustBadges.map((badge) => (
          <Box
            key={badge.id}
            sx={{ display: 'grid', gridTemplateColumns: '150px 1fr auto', gap: 1.5, mb: 1.5, alignItems: 'center' }}
          >
            <TextField
              select
              size="small"
              label="Icon"
              value={badge.icon}
              onChange={(e) => setBadge(badge.id, { icon: e.target.value })}
            >
              {ICON_OPTIONS.map((key) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {FOOTER_ICONS[key]} {key}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Text"
              value={badge.text}
              onChange={(e) => setBadge(badge.id, { text: e.target.value })}
            />
            <Switch
              checked={badge.visible}
              onChange={(e) => setBadge(badge.id, { visible: e.target.checked })}
            />
          </Box>
        ))}
      </Paper>

      {/* Link columns */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Link columns
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>
            Add column
          </Button>
        </Box>

        {[...content.columns]
          .sort((a, b) => a.order - b.order)
          .map((column) => (
            <Paper key={column.id} variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#FCFAFC' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <TextField
                  size="small"
                  label="Column heading"
                  value={column.heading}
                  onChange={(e) => setColumn(column.id, { heading: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <Tooltip title={column.visible ? 'Visible' : 'Hidden'}>
                  <Switch
                    checked={column.visible}
                    onChange={(e) => setColumn(column.id, { visible: e.target.checked })}
                  />
                </Tooltip>
                <Tooltip title="Delete column">
                  <IconButton size="small" color="error" onClick={() => removeColumn(column.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {[...column.links]
                .sort((a, b) => a.order - b.order)
                .map((link, index, arr) => (
                  <Box
                    key={link.id}
                    sx={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr auto', gap: 1, mb: 1, alignItems: 'center' }}
                  >
                    <TextField
                      size="small"
                      label="Label"
                      value={link.label}
                      onChange={(e) => setLink(column.id, link.id, { label: e.target.value })}
                    />
                    <TextField
                      size="small"
                      label="Link"
                      value={link.href}
                      onChange={(e) => setLink(column.id, link.id, { href: e.target.value })}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" disabled={index === 0} onClick={() => moveLink(column.id, link.id, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === arr.length - 1}
                        onClick={() => moveLink(column.id, link.id, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <Switch
                        size="small"
                        checked={link.visible}
                        onChange={(e) => setLink(column.id, link.id, { visible: e.target.checked })}
                      />
                      <IconButton size="small" color="error" onClick={() => removeLink(column.id, link.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => addLink(column.id)} sx={{ mt: 0.5 }}>
                Add link
              </Button>
            </Paper>
          ))}
      </Paper>

      {/* Contact */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Get in touch
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Column heading"
            value={content.contact.heading}
            onChange={(e) => setContactField('heading', e.target.value)}
          />
          <TextField
            label="Email"
            value={content.contact.email}
            onChange={(e) => setContactField('email', e.target.value)}
          />
          <TextField
            label="Phone"
            value={content.contact.phone}
            onChange={(e) => setContactField('phone', e.target.value)}
          />
          <TextField
            label="Support hours"
            value={content.contact.hours}
            onChange={(e) => setContactField('hours', e.target.value)}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />
        <TextField
          size="small"
          label="Payment label"
          value={content.contact.paymentLabel}
          onChange={(e) => setContactField('paymentLabel', e.target.value)}
          sx={{ mb: 1.5, width: 220 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          {content.contact.paymentMethods.map((method) => (
            <Chip
              key={method}
              label={method}
              onDelete={() =>
                patch((prev) => ({
                  ...prev,
                  contact: {
                    ...prev.contact,
                    paymentMethods: prev.contact.paymentMethods.filter((m) => m !== method)
                  }
                }))
              }
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Add payment method"
            value={paymentDraft}
            onChange={(e) => setPaymentDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && paymentDraft.trim()) {
                patch((prev) => ({
                  ...prev,
                  contact: {
                    ...prev.contact,
                    paymentMethods: [...prev.contact.paymentMethods, paymentDraft.trim()]
                  }
                }));
                setPaymentDraft('');
              }
            }}
          />
          <Button
            variant="outlined"
            disabled={!paymentDraft.trim()}
            onClick={() => {
              patch((prev) => ({
                ...prev,
                contact: {
                  ...prev.contact,
                  paymentMethods: [...prev.contact.paymentMethods, paymentDraft.trim()]
                }
              }));
              setPaymentDraft('');
            }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Bottom bar */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Bottom bar
        </Typography>
        <TextField
          fullWidth
          label="Copyright suffix"
          helperText={`Renders as: © ${new Date().getFullYear()} Vediluxe. <this text>`}
          value={content.bottom.copyrightSuffix}
          onChange={(e) =>
            patch((prev) => ({
              ...prev,
              bottom: { ...prev.bottom, copyrightSuffix: e.target.value }
            }))
          }
          sx={{ mb: 2 }}
        />
        {[...content.bottom.links]
          .sort((a, b) => a.order - b.order)
          .map((link) => (
            <Box
              key={link.id}
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr auto', gap: 1, mb: 1, alignItems: 'center' }}
            >
              <TextField
                size="small"
                label="Label"
                value={link.label}
                onChange={(e) => setBottomLink(link.id, { label: e.target.value })}
              />
              <TextField
                size="small"
                label="Link"
                value={link.href}
                onChange={(e) => setBottomLink(link.id, { href: e.target.value })}
              />
              <Switch
                size="small"
                checked={link.visible}
                onChange={(e) => setBottomLink(link.id, { visible: e.target.checked })}
              />
            </Box>
          ))}
      </Paper>

      <Button variant="contained" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save & publish'}
      </Button>
    </Box>
  );
};

export default AdminFooterPage;
